import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/auth/AuthContext";
import { notificationsApi } from "@/lib/notifications-api";
import { employeeApi } from "@/lib/employee-api";

/**
 * Cache key for the unread-count query. Exported so any page that mutates
 * notifications (mark-read / mark-all-read) can invalidate it and force the
 * header badge to refresh immediately instead of waiting for the next poll.
 */
export const UNREAD_NOTIFICATIONS_QUERY_KEY = ["notifications", "unread-count"] as const;

/**
 * Live unread-notification count for the current admin user. Polls every
 * 60 seconds while the tab is visible. Returns `0` when the user isn't
 * logged in so the header badge has something deterministic to render.
 */
export function useUnreadNotifications(): number {
  const { user } = useAuth();
  const enabled = !!user;

  const { data } = useQuery({
    queryKey: UNREAD_NOTIFICATIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await notificationsApi.unreadCount();
      return res.count;
    },
    enabled,
    // 60s is the slowest acceptable refresh for a badge; users who mark
    // things read in-app get an instant update via `invalidateUnreadCount`.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  return data ?? 0;
}

/**
 * Invalidate the unread-count query from anywhere in the tree. Call after
 * mark-read / mark-all-read mutations on the Notifications page.
 */
export function useInvalidateUnreadCount(): () => void {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: UNREAD_NOTIFICATIONS_QUERY_KEY });
  };
}

/**
 * The same badge for the employee lane (`/me`).
 *
 * A separate hook and a separate cache key because it is a different endpoint
 * and a different principal: `/mobile/notifications` returns the employee's
 * own notifications, while `/notifications` needs a `User` row that most
 * employees do not have. Sharing one key would let a dual-role person's admin
 * count leak into their employee badge, and vice versa.
 */
export const EMPLOYEE_UNREAD_QUERY_KEY = ["mobile-notifications", "unread-count"] as const;

export function useEmployeeUnreadNotifications(): number {
  const { employee } = useAuth();

  const { data } = useQuery({
    queryKey: EMPLOYEE_UNREAD_QUERY_KEY,
    queryFn: async () => (await employeeApi.unreadCount()).count,
    enabled: !!employee,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  return data ?? 0;
}

export function useInvalidateEmployeeUnread(): () => void {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: EMPLOYEE_UNREAD_QUERY_KEY });
  };
}
